/**
 * Egreso Mock Repository - Repositorio simulado para egresos
 * Para desarrollo y pruebas sin Firebase
 */

import { Egreso } from '../../../shared/types';
import { LoggerService } from '../../../shared/services/logger.service';

export class EgresoMockRepository {
  private egresos: Egreso[] = [];
  private listeners: { [userId: string]: ((egresos: Egreso[]) => void)[] } = {};

  constructor() {
    // Datos mock iniciales
    this.initializeMockData();
  }

  private initializeMockData(): void {
    const mockEgresos: Egreso[] = [
      {
        id: 'mock-egreso-1',
        userId: 'mock-user-1',
        monto: 15000,
        descripcion: 'Alquiler',
        categoria: 'Vivienda',
        fecha: new Date('2024-01-01'),
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01')
      },
      {
        id: 'mock-egreso-2',
        userId: 'mock-user-1',
        monto: 8000,
        descripcion: 'Supermercado',
        categoria: 'Alimentación',
        fecha: new Date('2024-01-05'),
        createdAt: new Date('2024-01-05'),
        updatedAt: new Date('2024-01-05')
      },
      {
        id: 'mock-egreso-3',
        userId: 'mock-user-1',
        monto: 5000,
        descripcion: 'Transporte',
        categoria: 'Transporte',
        fecha: new Date('2024-01-10'),
        createdAt: new Date('2024-01-10'),
        updatedAt: new Date('2024-01-10')
      },
      {
        id: 'mock-egreso-4',
        userId: 'mock-user-1',
        monto: 3000,
        descripcion: 'Entretenimiento',
        categoria: 'Entretenimiento',
        fecha: new Date('2024-01-15'),
        createdAt: new Date('2024-01-15'),
        updatedAt: new Date('2024-01-15')
      }
    ];

    this.egresos = mockEgresos;
  }

  subscribeToEgresos(userId: string, callback: (egresos: Egreso[]) => void): () => void {
    if (!this.listeners[userId]) {
      this.listeners[userId] = [];
    }
    
    this.listeners[userId].push(callback);
    
    // Notificar datos iniciales
    const userEgresos = this.egresos.filter(egreso => egreso.userId === userId);
    callback(userEgresos);
    
    LoggerService.debug('Mock egresos subscription added', 'EgresoMockRepository', { userId });
    
    // Retornar función de unsubscribe
    return () => {
      const index = this.listeners[userId].indexOf(callback);
      if (index > -1) {
        this.listeners[userId].splice(index, 1);
      }
      LoggerService.debug('Mock egresos subscription removed', 'EgresoMockRepository', { userId });
    };
  }

  async create(userId: string, egreso: Omit<Egreso, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<void> {
    LoggerService.info('Mock egreso creation', 'EgresoMockRepository', { userId, egreso });
    
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const newEgreso: Egreso = {
      ...egreso,
      id: `mock-egreso-${Date.now()}`,
      userId,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    this.egresos.push(newEgreso);
    this.notifyListeners(userId);
    
    LoggerService.logUserAction('mock_egreso_created', 'EgresoMockRepository', { userId, egresoId: newEgreso.id });
  }

  async update(userId: string, id: string, egreso: Partial<Omit<Egreso, 'id' | 'userId' | 'createdAt' | 'updatedAt'>>): Promise<void> {
    LoggerService.info('Mock egreso update', 'EgresoMockRepository', { userId, id, egreso });
    
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const index = this.egresos.findIndex(e => e.id === id && e.userId === userId);
    if (index === -1) {
      throw new Error('Egreso no encontrado');
    }
    
    this.egresos[index] = {
      ...this.egresos[index],
      ...egreso,
      updatedAt: new Date()
    };
    
    this.notifyListeners(userId);
    
    LoggerService.logUserAction('mock_egreso_updated', 'EgresoMockRepository', { userId, egresoId: id });
  }

  async delete(userId: string, id: string): Promise<void> {
    LoggerService.info('Mock egreso deletion', 'EgresoMockRepository', { userId, id });
    
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const index = this.egresos.findIndex(e => e.id === id && e.userId === userId);
    if (index === -1) {
      throw new Error('Egreso no encontrado');
    }
    
    this.egresos.splice(index, 1);
    this.notifyListeners(userId);
    
    LoggerService.logUserAction('mock_egreso_deleted', 'EgresoMockRepository', { userId, egresoId: id });
  }

  async getByCategory(userId: string, categoria: string): Promise<Egreso[]> {
    LoggerService.debug('Mock get by category', 'EgresoMockRepository', { userId, categoria });
    
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 200));
    
    return this.egresos.filter(egreso => 
      egreso.userId === userId && egreso.categoria === categoria
    );
  }

  async getByDateRange(userId: string, startDate: Date, endDate: Date): Promise<Egreso[]> {
    LoggerService.debug('Mock get by date range', 'EgresoMockRepository', { userId, startDate, endDate });
    
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 200));
    
    return this.egresos.filter(egreso => 
      egreso.userId === userId &&
      egreso.fecha >= startDate &&
      egreso.fecha <= endDate
    );
  }

  private notifyListeners(userId: string): void {
    if (this.listeners[userId]) {
      const userEgresos = this.egresos.filter(egreso => egreso.userId === userId);
      this.listeners[userId].forEach(callback => callback(userEgresos));
    }
  }
}
